import { getIn } from "formik";
import { useEffect, useMemo, useReducer, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { get_processes_list, get_users, splitApiRequestFormErrors } from "../api";
import type { User } from "../api";
import type { ChatEntry } from "../chat/chatStore";
import type { FilterOption } from "../components/FilterMultiSelect";
import { useLogin } from "../reducers";
import { buildVisibleUserOptions } from "../utils/permissions";
import { usePeopleCompanies } from "../utils/usePeopleCompanies";
import { create_alert, review_alert, run_alert } from "./api";
import { useScopeSummary } from "./hooks";
import type { AlertReview, Schedule, ScopeTouched, WatchlistScope } from "./types";
import { hasProcessSelection } from "./types";
import { WEEKLY_DAYS } from "./types";
import { initialWatchSetupState, watchSetupReducer } from "./watchSetupState";
import type { WatchSetupSource } from "./watchSetupState";

const UNTOUCHED_SCOPE: ScopeTouched = {
  people: false,
  processes: false,
  window: false,
  schedule: false,
};

/** How long the "saved" confirmation stays up before it withdraws itself. */
const SAVED_NOTICE_MS = 5000;

type AlertSaveFormValues = {
  prompt: string;
  schedule: { everyDays: number };
  scope: { window: { days: number } };
};

/**
 * Scope, cadence and the option lists behind them — the context every watch
 * surface shares, whether it is asking a question or setting up a watch.
 *
 * `defaultWindowDays` sets the untouched window: chat asks about the last
 * month by default, while watch setup keeps the week that pairs with the
 * default weekly cadence.
 */
export const useWatchContext = ({ defaultWindowDays = 7 }: { defaultWindowDays?: number } = {}) => {
  const { t } = useTranslation();
  const { user, permissions } = useLogin();
  const [userOptionsLoaded, setUserOptionsLoaded] = useState(false);
  const [users, setUsers] = useState<User[]>([user]);
  const [processOptions, setProcessOptions] = useState<FilterOption[]>([]);
  // Distinguishes "none discovered" from "still loading", for empty states.
  const [processOptionsLoaded, setProcessOptionsLoaded] = useState(false);
  const [scope, setScope] = useState<WatchlistScope>({
    userIds: [user.id],
    processSlugs: [],
    window: { days: defaultWindowDays },
  });
  const [schedule, setSchedule] = useState({ everyDays: WEEKLY_DAYS });
  const [scopeTouched, setScopeTouched] = useState<ScopeTouched>(UNTOUCHED_SCOPE);

  useEffect(() => {
    // Either of these failing leaves what the state was seeded with: the
    // viewer alone and no processes.
    get_users()
      .then(loaded => setUsers(loaded.items))
      .catch(() => undefined)
      .finally(() => setUserOptionsLoaded(true));
    get_processes_list()
      .then(loaded => {
        if (loaded.length > 0)
          setProcessOptions(loaded.map(process => ({ id: process.slug, label: process.name })));
        setProcessOptionsLoaded(true);
      })
      .catch(() => undefined);
  }, []);

  const companies = usePeopleCompanies(users);
  const userOptions = useMemo(
    () => buildVisibleUserOptions(permissions.recordings.visualization, users, user, t, companies),
    [permissions, users, user, t, companies],
  );
  const scopeSummary = useScopeSummary(userOptions, processOptions);

  const changeScope = (next: WatchlistScope) => {
    setScopeTouched(current => ({
      ...current,
      people:
        current.people ||
        next.userIds.length !== scope.userIds.length ||
        next.userIds.some(id => !scope.userIds.includes(id)),
      processes:
        current.processes ||
        next.allProcesses !== scope.allProcesses ||
        next.processSlugs.length !== scope.processSlugs.length ||
        next.processSlugs.some(id => !scope.processSlugs.includes(id)),
      window: current.window || next.window.days !== scope.window.days,
    }));
    setScope(next);
  };

  const changeSchedule = (next: { everyDays: number }) => {
    if (next.everyDays !== schedule.everyDays) {
      setScopeTouched(current => ({ ...current, schedule: true }));
    }
    setSchedule(next);
  };

  return {
    scope,
    setScope,
    schedule,
    setSchedule,
    scopeTouched,
    setScopeTouched,
    changeScope,
    changeSchedule,
    userOptions,
    processOptions,
    processOptionsLoaded,
    userOptionsLoaded,
    scopeSummary,
  };
};

type WatchContext = ReturnType<typeof useWatchContext>;

/**
 * Clarifies the KPI definition and saves it once no questions remain.
 * Shared by direct setup and conversation promotion; the caller renders
 * questions, progress, errors, and the saved confirmation.
 */
export const useWatchSetup = (
  context: WatchContext,
  { onSaved }: { onSaved?: () => void } = {},
) => {
  const { t } = useTranslation(undefined, { keyPrefix: "watchlist" });
  const { t: tRoot } = useTranslation();
  const {
    scope,
    setScope,
    schedule,
    setSchedule,
    scopeTouched,
    userOptions,
    processOptions,
    scopeSummary,
  } = context;

  const [setup, dispatchSetup] = useReducer(watchSetupReducer, undefined, initialWatchSetupState);
  const setupRevisionRef = useRef(0);
  const savingRef = useRef(false);
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  // Name of the alert just saved. Saving clears the box and the conversation
  // that led to it, so without this the whole exchange would simply vanish and
  // leave the page looking like nothing had happened.
  const [savedAlertId, setSavedAlertId] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [watchSaveError, setWatchSaveError] = useState<string | null>(null);

  // Long enough to read, short enough that it never becomes part of the
  // furniture: the alert itself is in the list, so the confirmation has
  // nothing left to say once it has been seen.
  useEffect(() => {
    if (!saved) return;
    const timer = setTimeout(() => setSaved(null), SAVED_NOTICE_MS);
    return () => clearTimeout(timer);
  }, [saved]);

  /**
   * Save a prompt as an alert and evaluate it straight away. Every alert is
   * born this way — from the composer with Watch on, or from an answer already
   * on screen — so none of them sits in the list with nothing to show.
   */
  const watchPrompt = (
    prompt: string,
    title: string,
    saveScope: WatchlistScope = scope,
    saveSchedule: Schedule = schedule,
  ) => {
    if (
      savingRef.current ||
      !prompt ||
      saveScope.userIds.length === 0 ||
      !hasProcessSelection(saveScope)
    )
      return;
    savingRef.current = true;
    setSaving(true);
    onSaved?.();
    setWatchSaveError(null);
    void create_alert({ prompt, title, scope: saveScope, schedule: saveSchedule })
      .then(id => {
        dispatchSetup({ type: "reset" });
        run_alert(id);
        setSavedAlertId(id);
        setSaved(title || prompt);
      })
      .catch(error => {
        const parsed = splitApiRequestFormErrors<AlertSaveFormValues>(
          error,
          tRoot,
          ["prompt", "schedule.everyDays", "scope.window.days"],
          {
            schedule: "schedule.everyDays",
            "schedule.every_days": "schedule.everyDays",
            "scope.window_days": "scope.window.days",
          },
        );
        setWatchSaveError(
          getIn(parsed.fieldErrors, "prompt") ??
            getIn(parsed.fieldErrors, "schedule.everyDays") ??
            getIn(parsed.fieldErrors, "scope.window.days") ??
            parsed.remainder,
        );
      })
      .finally(() => {
        savingRef.current = false;
        setSaving(false);
      });
  };

  const cancelSetup = () => {
    setupRevisionRef.current += 1;
    dispatchSetup({ type: "reset" });
  };

  const applyReview = (
    reviewed: AlertReview,
    source: WatchSetupSource,
    baseTranscript: ChatEntry[],
    clarificationTranscript: ChatEntry[],
    round: number,
  ) => {
    const allowedUsers = new Set(userOptions.map(option => option.id));
    const allowedProcesses = new Set(processOptions.map(option => option.id));
    const proposedUsers = reviewed.scope.userIds.filter(id => allowedUsers.has(id));
    const proposedProcesses = reviewed.scope.processSlugs.filter(id => allowedProcesses.has(id));
    const reviewedScope: WatchlistScope = {
      userIds: proposedUsers.length > 0 ? proposedUsers : scope.userIds,
      processSlugs: proposedProcesses,
      window: {
        days: Math.max(1, reviewed.scope.windowDays || 7),
      },
    };
    const reviewedSchedule = {
      everyDays: Math.max(1, reviewed.scope.scheduleEveryDays || WEEKLY_DAYS),
    };
    setScope(reviewedScope);
    setSchedule(reviewedSchedule);
    dispatchSetup({
      type: "reviewed",
      source,
      review: reviewed,
      round,
      baseTranscript,
      transcript: clarificationTranscript,
    });
    if (reviewed.questions.length === 0) {
      watchPrompt(reviewed.prompt.trim(), reviewed.title.trim(), reviewedScope, reviewedSchedule);
    }
  };

  /** The short review call shared by direct setup and conversation promotion. */
  const reviewWatch = (
    prompt: string,
    baseTranscript: ChatEntry[],
    clarificationTranscript: ChatEntry[] = [],
    source: WatchSetupSource = setup.source ?? "direct",
    round: number = setup.round,
  ) => {
    if (setup.reviewing || !hasProcessSelection(scope)) return;
    const revision = ++setupRevisionRef.current;
    dispatchSetup({ type: "set_reviewing", reviewing: true });
    setSaved(null);
    setWatchSaveError(null);
    void review_alert({
      prompt,
      scope: scopeSummary(scope, schedule),
      transcript: [...baseTranscript, ...clarificationTranscript],
      context: { scope, schedule, userOptions, processOptions, touched: scopeTouched },
    })
      .then(reviewed => {
        if (mounted.current && setupRevisionRef.current === revision)
          applyReview(reviewed, source, baseTranscript, clarificationTranscript, round);
      })
      .catch(() => {
        if (!mounted.current || setupRevisionRef.current !== revision) return;
        setWatchSaveError(tRoot("chat_kpi_review_failed"));
      })
      .finally(() => {
        if (mounted.current && setupRevisionRef.current === revision)
          dispatchSetup({ type: "set_reviewing", reviewing: false });
      });
  };

  const beginDirectSetup = () => {
    setSaved(null);
    dispatchSetup({ type: "begin", source: "direct" });
  };

  const beginConversationSetup = (entries: ChatEntry[]) => {
    dispatchSetup({ type: "begin", source: "conversation", baseTranscript: entries });
    reviewWatch(t("watch_from_conversation"), entries, [], "conversation", 1);
  };

  const nextSetupQuestion = () => {
    const { review, questionIndex, answers, transcript, baseTranscript, source, round, reviewing } =
      setup;
    if (!review || questionIndex === null || reviewing) return;
    if (questionIndex < review.questions.length - 1) {
      dispatchSetup({ type: "set_question", index: questionIndex + 1 });
      return;
    }
    const answerText = review.questions
      .map(question => `${question.question}\n${answers[question.id]?.answer ?? ""}`)
      .join("\n\n");
    const said: ChatEntry[] = [
      ...transcript,
      { kind: "message", role: "user", content: review.prompt },
      {
        kind: "message",
        role: "assistant",
        content: review.questions.map(question => question.question).join("\n"),
      },
      { kind: "message", role: "user", content: answerText },
    ];
    reviewWatch(review.prompt, baseTranscript, said, source ?? "direct", round + 1);
  };

  const backInSetup = () => {
    const { review, questionIndex, source } = setup;
    if (!review) {
      cancelSetup();
      return;
    }
    if (questionIndex !== null && questionIndex > 0) {
      dispatchSetup({ type: "set_question", index: questionIndex - 1 });
      return;
    }
    if (questionIndex === null && review.questions.length > 0) {
      dispatchSetup({ type: "set_question", index: review.questions.length - 1 });
      return;
    }
    if (source === "direct") {
      dispatchSetup({ type: "back_to_prompt" });
      return;
    }
    cancelSetup();
  };

  const clearNotices = () => {
    setSaved(null);
    setWatchSaveError(null);
  };

  return {
    setup,
    dispatchSetup,
    saved,
    savedAlertId,
    saving,
    dismissSaved: () => setSaved(null),
    watchSaveError,
    clearNotices,
    watchPrompt,
    reviewWatch,
    cancelSetup,
    beginDirectSetup,
    beginConversationSetup,
    nextSetupQuestion,
    backInSetup,
  };
};
